import { Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { Point, ScoutPath, ScoutPoint, ScoutPointLocalPhoto } from '../../../core/models';
import { ScoutPointPhotoDoc } from '../../../core/schema/scout-point-photo-schema';
import { ScoutTaskDoc } from '../../../core/schema/scout-task-schema';
import { BuildingDoc, BuildingService, ScoutPointLocalPhotoService, ScoutPointLocalPhotoUrlService, ScoutPointPhotoService, ScoutTaskService } from '../../../core/services';
import { AlignmentType, BorderStyle, Document, HeadingLevel, Media, Packer, PageOrientation, Paragraph, PictureRun, ShadingType, Table, TableCell, TableRow, TextRun, VerticalAlign, PageNumber, Footer, WidthType, ITableCellOptions  } from 'docx'
import { saveAs } from "file-saver";
import { ScoutPathImageComponent } from './scout-path-image/scout-path-image.component';
import { AzimuthHelper } from '../../../core/azimuth-helper';
import { skipWhile, take } from 'rxjs/operators';
import { AppTab } from '../../../core/app-tab';
import { PathPointParametersTableComponent } from './path-point-parameters-table/path-point-parameters-table.component';
import { ShowRequest } from '../../../core';

export class ReportPhoto {
  photoDoc : ScoutPointPhotoDoc
  photoFileDoc : ScoutPointLocalPhoto
  photoUrl : string //url encoded impage
}

export class PhotoGroup {
  photos : ReportPhoto[] = []
  title : string
  groupName: string
  sortOrder : number
}

export class ReportScoutPoint {
  scoutPoint : ScoutPoint
  photoGroups : PhotoGroup[] = []
}

class Dimensions {
  width: number
  height: number
}

@Component({
  selector: 'app-scout-path-report',
  templateUrl: './scout-path-report.component.html',
  styleUrls: ['./scout-path-report.component.scss']
})
export class ScoutPathReportComponent implements OnInit {

  @Input() scoutTaskDoc: ScoutTaskDoc
  @Input() scoutPathId: string
  @Input() public detailFn : (vi: ShowRequest) => void;
  
  @ViewChildren(ScoutPathImageComponent) plotterImages!: QueryList<ScoutPathImageComponent>;
  
  @Input() set tabConfig( tabConfig : AppTab ) {
    tabConfig.setSelected = this.onTabSelected
  }
  
  private urlParmSubscription: Subscription;
  building: BuildingDoc
  scoutPathStartPt: ScoutPoint
  //startPoint: PointDoc
  buildingPoints: Point[]
  scoutPath: ScoutPath
  scoutPathIndex : number
  segmentAScoutPoints: ScoutPoint[] = []
  segmentBScoutPoints: ScoutPoint[] = []
  reportScoutPoints: ReportScoutPoint[] = []
  pathNumber: number
  bProcessing = false
  errorMsg = ''
  pendingPlotterImageCnt = 0

  @ViewChild('alterPhotoCanvas') alterPhotoCanvas: ElementRef;
  @ViewChild('pathPointParamTable') pathPointParamTable : PathPointParametersTableComponent;
  
  
  cornerCompassDirection : string
  cornerDirections : string[] = ['','']

  //Expose AzimuthHelper to template
  AzimuthHelper = AzimuthHelper
  
  wideMapZoomLevel = 19
  detailMapZoomLevel : number = null
  leafletMapToImageWaitSecs = 10
  imageQuality = 0.85
  
  bStandalonePageUrl = false

  mapDefs = []

  bShowPhotos = false
  
  constructor(
    private scoutTaskService: ScoutTaskService,
    private buildingService: BuildingService,
    private photoService: ScoutPointPhotoService,
    private localPhotoService: ScoutPointLocalPhotoService,
    private localPhotoUrlService: ScoutPointLocalPhotoUrlService,
    private route: ActivatedRoute,
    private titleService: Title
  ) {}


  ngOnInit() {  
    let appDiv : any = document.getElementsByClassName('app-content')[0];
    if ( appDiv && appDiv.style ) {
      appDiv.style.backgroundColor = 'white'
    }
  
    this.scoutTaskService.doWhenReady( async ()=>{

      try{

        const urlSegments = await this.route.url.pipe(take(1)).toPromise()
        this.bStandalonePageUrl = urlSegments.length > 0 && urlSegments[0].path == 'path-report'
        if ( this.bStandalonePageUrl ) {
          this.titleService.setTitle('Testbed Scout')
        }

        const queryParams = await this.route.queryParams.pipe(take(1)).toPromise()
        if ( queryParams.wideMapZoomLevel ) {
          this.wideMapZoomLevel = queryParams.wideMapZoomLevel
        }
        if ( queryParams.detailMapZoomLevel ) {
          this.detailMapZoomLevel = queryParams.detailMapZoomLevel
        }
        if ( queryParams.leafletMapToImageWaitSecs ) {
          this.leafletMapToImageWaitSecs = queryParams.leafletMapToImageWaitSecs
        }
        if ( queryParams.maxImageWidth ) {
          this.maxImageWidth = queryParams.maxImageWidth
        }
        if ( queryParams.maxImageHeight ) {
          this.maxImageHeight = queryParams.maxImageHeight
        }
        if ( queryParams.imageQuality ) {
          this.imageQuality = queryParams.imageQuality
        }
      } catch ( error ) {
        console.error(error)        
        this.errorMsg = 'Error occured getting report parameters from URL.'
        this.errorMsg += ' ' + this.scoutTaskService.extractErrorMsg(error)
        return 
      }

      this.urlParmSubscription = this.route.paramMap.subscribe( async (params: Params) => {
        const scoutTaskId = params.get('taskId')
        const scoutPathId = params.get('itemId')

        if ( scoutPathId && scoutPathId != this.scoutPathId ) {
          this.scoutPathId = params.get('itemId')
        }
        if ( scoutTaskId && scoutTaskId != this.scoutTaskDoc?._id ){
          this.scoutTaskDoc = await this.scoutTaskService.getById(scoutTaskId)
        }

        if ( !this.scoutTaskDoc ) {
          this.errorMsg = 'Could not find scoutTask by ID: ' + scoutTaskId
        } else {
          if ( this.bStandalonePageUrl ) {
            await this.initialize()
          }
        }        
      });
    })
  }


  ngOnDestroy() {
    this.urlParmSubscription.unsubscribe();
  }


  bInitialized = false
  initialize() {
    if ( !this.bInitialized ) {
      this.bInitialized = true
      this.generate()
    }    
  }

  
  processingMsg = ''
  failedDownloadCnt = 0

  async generate() {
    this.bProcessing = true
    this.failedDownloadCnt = 0
    this.processingMsg = 'Building report...'
    try {
      this.scoutPathIndex = this.scoutTaskDoc.scoutPaths.findIndex(sp=>sp.id==this.scoutPathId)
      this.scoutPath = this.scoutTaskDoc.scoutPaths[this.scoutPathIndex]
      this.pendingPlotterImageCnt = 0

      this.mapDefs = [{
        title: 'Path Detail',
        zoomLevel: this.detailMapZoomLevel,
        style: { width: '600px', height: '600px' },
        wordDoc: { width: 490, height: 490 },
        overlay: null
      },
      {
        title: 'Building Surroundings',
        zoomLevel: this.wideMapZoomLevel,
        style: { width: '600px', height: '600px' },
        wordDoc: { width: 490, height: 490 },
        overlay: null
      }]
  
      if ( this.scoutPath.overlay && this.scoutPath.overlay?.imageId && this.scoutPath.overlay?.pixelSize?.width) {
        this.mapDefs.push({
            title: 'Path Overlay',
            zoomLevel: this.detailMapZoomLevel,
            style: { width: '600px', height: '600px' },
            wordDoc: { width: 490, height: 490 },
            overlay: this.scoutPath.overlay
        })
        this.mapDefs.push({
          title: 'Path Overlay and Surroundings',
          zoomLevel: this.wideMapZoomLevel,
          style: { width: '600px', height: '600px' },
          wordDoc: { width: 490, height: 490 },
          overlay: this.scoutPath.overlay
        })
      }
      this.pendingPlotterImageCnt += this.mapDefs.length
      this.pathNumber = this.scoutTaskDoc.scoutPaths.findIndex(sp=>sp.id==this.scoutPathId) + 1
      if ( this.bStandalonePageUrl ) {
        this.titleService.setTitle('Testbed Scout ' + this.getPathName())
      }
      this.pathPointParamTable.populate()
      this.scoutPathStartPt = this.scoutTaskService.getScoutPathStartPoint(this.scoutPath)
      const scoutPathStartPtIndex = this.scoutPath.scoutPoints.findIndex(p=>p.id==this.scoutPathStartPt.id)
      //this.startPoint = this.scoutPathStartPt.pointId ? await this.pointService.rxCollection.findOne(this.scoutPathStartPt.pointId).exec() : null
      this.buildingPoints = [] // [this.startPoint] //for plotter without edit mode enabled, just need HA start point info
      this.scoutTaskService.updateScoutPathContext(this.scoutPath,this.buildingPoints)
      this.segmentAScoutPoints = this.scoutPath.scoutPoints.slice(scoutPathStartPtIndex+1)
      this.segmentBScoutPoints = this.scoutPath.scoutPoints.slice(0,scoutPathStartPtIndex).reverse()

      const shouldWaitForMaps = (waitedSecs:number) => {
        if ( waitedSecs >= 2*this.leafletMapToImageWaitSecs ) {
          return false
        } else {
          return this.pendingPlotterImageCnt>0
        }
      }
      const intervalMs = 1000
      await timer(0,intervalMs).pipe( skipWhile(timerCnt=>shouldWaitForMaps(intervalMs/1000*timerCnt)), take(1) ).toPromise()

      if ( !this.scoutPath ) {
        this.errorMsg = 'Valid scout task but could not find scout path by ID: ' + this.scoutPathId
      } else {
        this.building = await this.buildingService.rxCollection.findOne(this.scoutTaskDoc.buildingId).exec()
        this.reportScoutPoints = []
        let index = 1
        for( let scoutPoint of this.scoutPath.scoutPoints ) {
          this.processingMsg = `Processing point ${scoutPoint.name} (${index++} of ${this.scoutPath.scoutPoints.length})`
          const photoDocs = await this.photoService.rxCollection.find().where('scoutPointId').eq(scoutPoint.id).exec()
          let ptReportPhotos = new ReportScoutPoint()
          ptReportPhotos.scoutPoint = scoutPoint
          let allDownloads : Promise<void>[] = []
          for( let photoDoc of photoDocs ) {
            let reportPhoto = new ReportPhoto()
            reportPhoto.photoDoc = photoDoc
            if ( photoDoc.fileName ) {
              reportPhoto.photoFileDoc = await this.localPhotoService.getById(photoDoc._id)
              if ( reportPhoto.photoFileDoc ) {
                reportPhoto.photoUrl = (await this.localPhotoUrlService.getById(photoDoc._id))?.url;
              }
              if ( !reportPhoto.photoUrl ) {
                const promise = new Promise<void>( (completeFn,errorFn) => {
                  this.photoService.downloadScoutRefPhotoFile(photoDoc._id,photoDoc.mimeType).then(
                    async dataUrl => this.photoService.saveLocalPhotoFile(photoDoc,dataUrl).then(
                      photoFileDoc => {
                        reportPhoto.photoFileDoc = photoFileDoc
                        reportPhoto.photoUrl = dataUrl
                        completeFn()
                      },
                      error => {
                        console.error('Failed saving report photo locally: ' + photoDoc._id)
                        errorFn(error)
                      }
                    ),
                    error => {
                      console.error('Failed downloading report photo: ' + photoDoc._id)
                      errorFn(error)
                    }
                  )
                })
                allDownloads.push(promise)
              }
              let photoGrp = ptReportPhotos.photoGroups.find(grp=>grp.groupName==photoDoc.group)
              if ( !photoGrp ) {
                photoGrp = new PhotoGroup()
                const grpTitles = {
                  'measurement': { title: "Reference Measurement Photos", sortOrder: 1 },
                  'wide-angle': { title: "Wide Angle Photos", sortOrder: 2 },
                  'point-to-point': { title: "Path View Photos", sortOrder: 3 },
                  'additional': { title: "Additional Photos", sortOrder: 4 }
                }
                photoGrp.title = grpTitles[photoDoc.group]?.title || photoDoc.group
                photoGrp.sortOrder = grpTitles[photoDoc.group]?.sortOrder
                photoGrp.groupName = photoDoc.group
                ptReportPhotos.photoGroups.push(photoGrp)
              }
              photoGrp.photos.push(reportPhoto)
            }
          }
          // show measurment group first and  additional group last
          ptReportPhotos.photoGroups.sort((a,b) => (a.sortOrder>b.sortOrder)?1:-1); 
          for( var photoGrp of ptReportPhotos.photoGroups ) {
            photoGrp.photos.sort((a,b)=> (a.photoDoc.groupOrderIndex > b.photoDoc.groupOrderIndex)? 1 : -1 )
          }
          this.reportScoutPoints.push(ptReportPhotos)
          const failedCnt = (await Promise.allSettled(allDownloads)).filter(state=>state.status=="rejected").length
          if ( failedCnt ) {
            console.warn('Photo(s) failed loading while building report.')
            this.failedDownloadCnt += failedCnt
          }
          this.cornerCompassDirection = AzimuthHelper.getCompassDirectionTitle(this.scoutPath.azimuthCorner)
          this.cornerDirections = AzimuthHelper.getCornerDirections(this.scoutPath.azimuthCorner)
        }
      }
    } catch ( error ) {
      console.error(error)        
      this.errorMsg = 'Error occured building report. taskId: ' + this.scoutTaskDoc?._id + ', pathId: ' + this.scoutPathId
      this.errorMsg += ' ' + this.scoutTaskService.extractErrorMsg(error)        
    }
    this.processingMsg = ''
    this.bProcessing = false
  }


  rebuildReport() {
    this.generate()
  }


  onTabSelected = ( bSelected : boolean ) => {
    if ( bSelected ) {
      this.initialize()
    } else {
      //this.unsubscribe()
    }
  }


  /////////////////////////////////////////////////////////////////////////////////////////////////
  // Begin MS Word DOCX

  readonly HeaderColor = "C6E0B4"
  readonly FooterColor = "F8CBAD"
  readonly HighlightFillColor = 'FFF200'
  readonly DisabledPtTextColor = '880000'
  readonly DefaultFontSize = 20
  readonly TableTitleFontSize = 24
  readonly SmallFontSize = 14
  readonly DefaultFont = 'Calibri'
  readonly HighlightShading = {
    fill: this.HighlightFillColor,
    val: ShadingType.CLEAR                  
  };
  readonly ParagraphHighlightShading = {
    type: ShadingType.CLEAR,
    fill: this.HighlightFillColor,
    color: '000000'
  }
  bBuildingWordDoc = false
  readonly noBorderTableCellOption = {
    top: { style: BorderStyle.NONE, size: 1, color: "white" },
    bottom: { style: BorderStyle.NONE, size: 1, color: "white" },
    left: { style: BorderStyle.NONE, size: 1, color: "white" },
    right: { style: BorderStyle.NONE, size: 1, color: "white" }
  }


  async downloadWordDoc() {
    
    this.bBuildingWordDoc = true
    try{
    const doc = new Document({
      title: 'Scout Path Report - ' + this.getPathName(),      
      styles: {      
        paragraphStyles: [
          {
            id: "default",
            run: {
              bold: false,
              font: this.DefaultFont,
              size: this.DefaultFontSize
            },
          },
          {
            id: "defaultBold",
            run: {
              bold: true,
              font: this.DefaultFont,
              size: this.DefaultFontSize
            },
          },
          {
            id: "tableTitle",
            run: {
              bold: true,              
              font: this.DefaultFont,
              size: this.TableTitleFontSize
            },
          },
          {
            id: "disabledPtParagraph",
            run: {
              bold: true,              
              font: this.DefaultFont,
              size: this.TableTitleFontSize,
              color: this.DisabledPtTextColor,
              shading: this.ParagraphHighlightShading
            },
          },
          {
            id: "sectionTitle",
            run: {
              bold: true,              
              font: this.DefaultFont,
              size: 28
            },
          }
        ]      
      }
    });

    const defaultCellMargins = {top: 10, bottom: 10, left:50,right:50}
    const formatGeoAngle = (angle:any) => { return angle ? Number(angle).toFixed(6) : '' }

    const createKeyValueTable = ( dict : any[] ) => {
      return new Table({
        rows: dict.map( def=> new TableRow({ 
          children: [ 
            new TableCell({
              shading: {fill: this.HeaderColor},
              children: [ 
                new Paragraph({
                  style: "defaultBold",
                  children: [ 
                    new TextRun(def.key)
                  ], 
                  alignment: AlignmentType.RIGHT}) ], 
                  margins: defaultCellMargins
                }),
                new TableCell({
                  children: [ new Paragraph( {
                    style: 'default',
                    children: [ 
                      new TextRun('' + (def.value || ''))
                    ]})],
                  margins: defaultCellMargins
                }),
          ]
        }))
      })
    }

    const wordText = (text:any) : string => text ? `${text}` : ""

    const overviewDict = [
      { key: "Address", value: this.building?.civicAddress},
      { key: "Category", value: this.building?.category},
      { key: "Construction", value: this.building?.construction},
      { key: "Coordinates", value: `${this.building?.civicLatitude}, ${this.building?.civicLongitude}` },
      { key: "Description", value:this.scoutPath?.description }
    ]    
    const overviewTable = createKeyValueTable(overviewDict)

    
    //const azimuth1CornerAngle = AzimuthHelper.directionAngles[cornerDirections[0]]
    //const azimuth2CornerAngle = AzimuthHelper.directionAngles[cornerDirections[1]]
    let azimuthTableRows = [
      new TableRow({ 
        children: [ 
          new TableCell({
            shading: {fill: this.HeaderColor},
            children: [ 
              new Paragraph({
                children: [ 
                  new TextRun({
                    text:'Start Point',bold: true,
                    font: this.DefaultFont, size: this.DefaultFontSize
                  })
                ], 
              })
            ],
            margins: defaultCellMargins
          }),
          new TableCell({
            shading: {fill: this.HeaderColor},
            children: [ 
              new Paragraph({
                children: [ 
                  new TextRun({
                    text: this.cornerCompassDirection ?  (this.cornerCompassDirection + ' corner') : '      ',
                    bold: true,
                    font: this.DefaultFont,
                    size: this.DefaultFontSize
                  }) 
                ], 
              })
            ],
            margins: defaultCellMargins
          })
        ]
      }),
      new TableRow({ 
        children: [ 
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'defaultBold',
                text:'Exterior Wall One'
              })
            ],
            margins: defaultCellMargins
          }),
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'default',
                text: this.scoutPath.azimuth1Angle !== undefined ? 
                `${this.cornerDirections[0]} <-> ${AzimuthHelper.getOppositeDirection(this.cornerDirections[0])} (${this.scoutPath.azimuth1Angle.toFixed(2)}° ${this.cornerDirections[0]}) <-> (${((this.scoutPath.azimuth1Angle + 180) % 360).toFixed(2)}° ${AzimuthHelper.getOppositeDirection(this.cornerDirections[0])})` : 
                '      '
              })
            ],
            margins: defaultCellMargins
          }),
        ]
      }),
      new TableRow({ 
        children: [ 
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'defaultBold',
                text:'Exterior Wall Two'
              })
            ],
            margins: defaultCellMargins
          }),
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'default',
                text: this.scoutPath.azimuth2Angle !== undefined ? 
                `${this.cornerDirections[1]} <-> ${AzimuthHelper.getOppositeDirection(this.cornerDirections[1])} (${this.scoutPath.azimuth2Angle.toFixed(2)}° ${this.cornerDirections[1]}) <-> (${((this.scoutPath.azimuth2Angle + 180) % 360).toFixed(2)}° ${AzimuthHelper.getOppositeDirection(this.cornerDirections[1])})` : 
                '      '
              })
            ],
            margins: defaultCellMargins
          }),
        ]
      })      
    ]

    this.scoutPath.addlAzimuths?.forEach( addlAzimuth => {
      azimuthTableRows.push(new TableRow({ 
        children: [ 
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'defaultBold',
                text:addlAzimuth.title
              })
            ],
            margins: defaultCellMargins
          }),
          new TableCell({ 
            children: [
              new Paragraph({
                style: 'default',
                text: `${AzimuthHelper.getOppositeAngle(addlAzimuth.angle).toFixed(2)}° <-> ${addlAzimuth.angle.toFixed(2)}°`
              })
            ],
            margins: defaultCellMargins
          }),
        ]
      }))      
    })

    const azimuthsTable = new Table({
      rows: azimuthTableRows
    })
      
    const azimuthsTitle = new Paragraph({
      style: 'tableTitle',
      text: "Exterior Wall Azimuths"
    })
    
    const haPointDict = [
      { key: "ID", value: this.scoutPathStartPt.pointId || this.scoutPathStartPt.exPointId },
      { key: "Latitude", value: formatGeoAngle(this.scoutPathStartPt.latitude) },
      { key: "Longitude", value: formatGeoAngle(this.scoutPathStartPt.longitude) },
      { key: "Elevation", value: this.scoutPathStartPt.altitudeEllipsoidMeters + ' meter(s)' }
    ]    
    const haPointTable = createKeyValueTable(haPointDict)

    const getPointIdAsText = ( scoutPoint: ScoutPoint ) => {
      let pointIdText : string
      if ( scoutPoint.type == 'VIA' ) {
        pointIdText = 'VIA'
      } else if ( scoutPoint.type == 'HA' && scoutPoint.pointId ) {
        pointIdText = scoutPoint.pointId;
      } else {
        pointIdText = scoutPoint.exPointId || scoutPoint.type
      }
      return pointIdText
    }

    const createWayPointsTable = ( pts : ScoutPoint[] ) : Table => {
      const columnNames = ['Point', 'From', 'Horiz Dist.', 'Horiz Error','Bearing', 
          'Height Offset', 'Vert Error', 
          'H Impl Err', 'H Angle Err', 'Wall Angle Err',
          'V Impl Err', 'Floor Err', 'Unlevel Floor Err',
          'Location'
        ]
      return new Table({
        rows: [
          new TableRow({
            children: columnNames.map(columnName=>new TableCell({
              shading:{ fill: this.HeaderColor },
              children: [ 
                new Paragraph({
                  style: 'defaultBold',
                  text:columnName,
                  alignment: AlignmentType.CENTER                  
                })
              ],              
              margins: defaultCellMargins,
              verticalAlign: VerticalAlign.CENTER
            }))            
          }),
          ...pts.map(pt=> {
            let nameCell = new TableCell({                  
              children: [ new Paragraph({
                children: [ new TextRun({
                  text: ''+ pt.name,
                  color: 'white',
                  font: this.DefaultFont,
                  size: this.DefaultFontSize
                })],
                style: 'default',
                alignment: AlignmentType.CENTER
                })
              ],
              shading: {
                fill: pt.color.replace('#',''),
                val: ShadingType.CLEAR
              }
            })
            const createCell = (value:any, options: Partial<ITableCellOptions> = {}) => {
              if ( pt.disabled && !options.shading) {
                options = {...options, shading: this.HighlightShading}
              }
              return new TableCell({children: [ 
                new Paragraph({
                  children: [
                    new TextRun({
                      text:''+ value,
                      font: this.DefaultFont,
                      size: this.DefaultFontSize,
                      color: pt.disabled ? this.DisabledPtTextColor : '000000',
                      italics: pt.disabled
                    })
                  ],
                  alignment: AlignmentType.CENTER
                }) 
              ], ...options
            })
            }
            let exPtNameCell = createCell(getPointIdAsText(pt), { shading: { fill: this.FooterColor }});
            return new TableRow({
              children: [
                nameCell,
                createCell(pt.parentName),
                createCell(pt.horizontalDistance?.toFixed(3)),
                createCell(pt.horizontalError.cumulativeEstimate?.toFixed(3)),
                createCell(pt.bearing?.toFixed(3)),
                createCell(pt.heightOffset),
                createCell(pt.verticalError.segmentEstimate?.toFixed(3)),
                createCell(pt.horizontalError.implEstimate?.toFixed(3)),
                createCell(pt.horizontalError.measuredAngleEstimate?.toFixed(3)),
                createCell(pt.horizontalError.derivedFromExternalWall?pt.horizontalError.externalWallAngleEstimate?.toFixed(3):' - '),
                createCell(pt.verticalError.implEstimate?.toFixed(3)),
                createCell(pt.verticalError.floorVarianceEstimate?.toFixed(3)),
                createCell(pt.verticalError.unlevelFloor?pt.verticalError.unlevelFloorEstimate?.toFixed(3):' - '),
                exPtNameCell
              ]
            })
          })
        ]
      })
    }

    const pathSegmentATable = createWayPointsTable(this.segmentAScoutPoints)
    const pathSegmentBTable = createWayPointsTable(this.segmentBScoutPoints)

    const noBorderTableCellOption = this.noBorderTableCellOption

    const pageMargins = {
      top: 100,
      right: 900,
      bottom: 100,
      left: 900,
    }
    
    const innerTableMargins = { top: 0, bottom: 0, left: 100, right: 100 }
    
    let haStartPointCellChildren = [
      new Paragraph({
        style: this.scoutPathStartPt.disabled ? 'disabledPtParagraph' : 'tableTitle',
        text: (this.scoutPathStartPt.disabled?'DISABLED ': '') + "High Accuracy Point",        
      }),
      haPointTable
      ]

    if ( !this.scoutPathStartPt.pointId ) {
      haStartPointCellChildren.push(
        new Paragraph("* Approximate Coordinates")
      )
    }

    const createDisabledPtsChildren = () => {
      const disabledPtNames = this.scoutPath.scoutPoints.filter(pt => pt.disabled).map(pt=>pt.pointId||pt.exPointId||pt.type);
      disabledPtNames.sort();
      return [
        new Paragraph({
          style: 'disabledPtParagraph',
          text: `* Disabled Points: ${disabledPtNames.join(', ')}`,        
        }),
      ];
    }
    
    const haPointAndSegmentTablesTable = new Table({
      rows: [
        new TableRow({
          children: [ 
            new TableCell({children: [
              new Table({
                rows: [
                  new TableRow({ children: [
                    new TableCell({children: haStartPointCellChildren,
                      verticalAlign: VerticalAlign.TOP,
                      borders: noBorderTableCellOption,
                      margins: { ...innerTableMargins, left: 10 }
                    }),
                    new TableCell({ children: [ azimuthsTitle, azimuthsTable ],
                      verticalAlign: VerticalAlign.TOP,
                      borders: noBorderTableCellOption,
                      margins: { ...innerTableMargins, left: 10 }}),
                    new TableCell({ 
                      children: createDisabledPtsChildren(),
                      verticalAlign: VerticalAlign.BOTTOM,
                      borders: noBorderTableCellOption,
                      margins: { ...innerTableMargins, left: 40 }
                    })
                  ]})
                ]
              })
            ],
            margins: {top:0,bottom:0,left:0,right:0},
            borders: noBorderTableCellOption,
            verticalAlign: VerticalAlign.TOP
          })
        ]
        }),
        new TableRow({
          children: [         
            new TableCell({children: [
              new Paragraph({children:[ new TextRun(" ")]}),
              new Paragraph({
                style: 'tableTitle',
                text: "Path Segment A"
              }),
              pathSegmentATable
              ],
              verticalAlign: VerticalAlign.TOP,              
              borders: noBorderTableCellOption,
              margins: {top:0,bottom:0,left:0,right:0} 
            })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({children:[ new TextRun(" ")]}),
                new Paragraph({
                  style: 'tableTitle',
                  text: "Path Segment B"
                }),
                pathSegmentBTable
              ],
              verticalAlign: VerticalAlign.TOP,
              borders: noBorderTableCellOption,
              margins: {top:0,bottom:0,left:0,right:0} 
            }) 
          ]
        })
      ]

    })


    doc.addSection({
      size: {
        orientation: PageOrientation.LANDSCAPE,
      },
      margins: pageMargins,
      children: [        
        new Paragraph({
          style: 'sectionTitle',
          text: "In-Building Test Path Identification Sheet",
          heading: HeadingLevel.TITLE
        }),
        new Paragraph({
          style: 'tableTitle',
          children: [
            new TextRun({
              text: "Location " + this.getPathName(),
            }).break(),
            new TextRun({
              text: (this.scoutPath.floor ? 'Floor ' + this.scoutPath.floor + ' @ ': '') + this.building?.commonName,
              bold: false,
              size: 18                
            }),
          ]
        }),
        overviewTable,        
        new Paragraph({children:[ new TextRun(" ")]}),
        haPointAndSegmentTablesTable
      ],
      footers: {
        default: new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            children: ["Page: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                            children: [" of ", PageNumber.TOTAL_PAGES],
                        }),
                    ],
                }),
            ],
        }),
    }
    })
    
    ////////////////////////////////////////////////////////////////////////////////
    // screen snapshot of path plotter

    const visualizationChildren = [ new TextRun(' ').break()]

    let plotterImageControls = this.plotterImages.toArray()
    
    for( let i = 0; i < 2 && i < plotterImageControls.length; i++ ) {
      const plotterImage = Media.addImage(doc, 
          plotterImageControls[i].plotterImageDataUrl, 
          this.mapDefs[i].wordDoc.width, 
          this.mapDefs[i].wordDoc.height)
      if ( i == 0 ) {
        plotterImage.break()
      }
      visualizationChildren.push(plotterImage)
      if ( !(i%2) ) {
        visualizationChildren.push( new TextRun('  ') )
      }
    }

    doc.addSection({
      size: {
        orientation: PageOrientation.LANDSCAPE,
      },
      margins: pageMargins,
      children: [
        new Paragraph({
          text: "Extrapolated Test Point Calculator",
          heading: HeadingLevel.HEADING_1,
          style: 'sectionTitle'
        }), 
        new Paragraph({
          style: 'sectionTitle',
          text:"Visualization",
          heading: HeadingLevel.HEADING_2,
          children: visualizationChildren
        }),
      ],
      footers: {
        default: new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            children: ["Page: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                            children: [" of ", PageNumber.TOTAL_PAGES],
                        }),
                    ],
                }),
            ],
        }),
    }
    });
    

    ////////////////////////////////////////////////////////////////////////////////
    // Big table of all point data

    const createSimpleCell = (str:string, 
      opts: {
        paragraphAlignment?: AlignmentType;
        width?: number;
        docxCellOpts?: Partial<Omit<ITableCellOptions,'children'>>;
      } = {}
      ) => {
      if ( opts.paragraphAlignment == null ) {
        opts.paragraphAlignment = AlignmentType.CENTER;
      }
      let p = new Paragraph({
        children: [ 
          new TextRun({
            text: str,
            size: this.SmallFontSize,
            font: this.DefaultFont,          
          })
        ],
        alignment: opts.paragraphAlignment
      })

      let tableCellOptions : any = { 
        children:[ p ], 
        margins: { top: 0, left: 25, bottom: 0, right: 25 },         
        verticalAlign: VerticalAlign.CENTER,
        ...opts.docxCellOpts
      }
      if ( opts.width ) {
         tableCellOptions.width = {
           size: opts.width,
           type: WidthType.DXA
         }
      }
      let cell = new TableCell( tableCellOptions )
      return cell
    }

    doc.addSection({
      size: {
        orientation: PageOrientation.LANDSCAPE,
      },      
      margins: pageMargins,
      children: [
        new Paragraph({
          style: 'sectionTitle',
          text: "Individual Test Point Parameters",
          heading: HeadingLevel.HEADING_2,
          children:[

          ],
        }),
        new Table({          
          rows: [
            new TableRow({ 
              tableHeader: true,
              children: 
                this.pathPointParamTable.columnNames.map(columnName=> new TableCell({
                  children: [ new Paragraph({
                    children:[ 
                      new TextRun({
                        text:''+ columnName,bold:true,
                        size: this.SmallFontSize,
                        font: this.DefaultFont
                      })
                    ],                     
                    alignment: AlignmentType.CENTER}),                    
                  ],
                  shading: { 
                    fill: this.HeaderColor,
                    color: 'green',
                    val: ShadingType.CLEAR    
                  },
                  margins: { top: 0, left: 25, bottom: 0, right: 25 },        
                  verticalAlign: VerticalAlign.CENTER
                }))
            }),
            ...this.scoutPath.scoutPoints.map(scoutPoint=>{
              const opts = scoutPoint.disabled ? {docxCellOpts:{
                shading: this.HighlightShading, 
              }} : {};
              if ( scoutPoint.type == 'VIA') {
                return new TableRow({
                  children:[
                    createSimpleCell(scoutPoint.parentName,opts),
                    createSimpleCell(scoutPoint.name,opts),
                    createSimpleCell(getPointIdAsText(scoutPoint), {...opts, paragraphAlignment: AlignmentType.LEFT, width:1100}),
                    createSimpleCell("",opts),
                    createSimpleCell(""+formatGeoAngle(scoutPoint.latitude),{...opts, width:770}),
                    createSimpleCell(""+formatGeoAngle(scoutPoint.longitude), {...opts, width:770}),
                    createSimpleCell(""+Number(scoutPoint.altitudeEllipsoidMeters||0).toFixed(3),{...opts, width:550}),
                    createSimpleCell(""+Number(scoutPoint.horizontalError.cumulativeEstimate||0).toFixed(3),opts),
                    createSimpleCell(""+Number(scoutPoint.verticalError.segmentEstimate||0).toFixed(3),opts),
                    createSimpleCell("",opts),
                    createSimpleCell("",opts),
                    createSimpleCell("",opts),
                    createSimpleCell("",opts),
                    createSimpleCell("",opts),
                    createSimpleCell("",opts),
                    createSimpleCell("", {...opts,paragraphAlignment: AlignmentType.LEFT}),
                    createSimpleCell("", {...opts,paragraphAlignment: AlignmentType.LEFT}),
                    createSimpleCell("", {...opts,paragraphAlignment: AlignmentType.LEFT}),
                  ]
                })
              } else {
                return new TableRow({
                  children:[
                    createSimpleCell(scoutPoint.parentName,opts),
                    createSimpleCell(scoutPoint.name,opts),
                    createSimpleCell(getPointIdAsText(scoutPoint), {...opts,paragraphAlignment: AlignmentType.LEFT, width: 1100}),
                    createSimpleCell(scoutPoint.envData?.bInterior ? 'Interior' : (scoutPoint.envData?.bInterior === false ? 'Exterior' : ''),opts),
                    createSimpleCell(""+formatGeoAngle(scoutPoint.latitude),{...opts, width: 770}),
                    createSimpleCell(""+formatGeoAngle(scoutPoint.longitude), {...opts, width: 770}),
                    createSimpleCell(""+Number(scoutPoint.altitudeEllipsoidMeters||0).toFixed(3),{...opts, width: 550}),
                    createSimpleCell(""+Number(scoutPoint.horizontalError.cumulativeEstimate||0).toFixed(3),opts),
                    createSimpleCell(""+Number(scoutPoint.verticalError.segmentEstimate||0).toFixed(3),opts),
                    createSimpleCell(""+scoutPoint.envData?.nearestWindowDistance,opts),
                    createSimpleCell(""+scoutPoint.envData?.windowCount, opts),
                    createSimpleCell(""+scoutPoint.refData?.location1Distance, opts),
                    createSimpleCell(""+scoutPoint.refData?.location2Distance, opts),
                    createSimpleCell(""+scoutPoint.envData?.floorToCeilingHeight, opts),
                    createSimpleCell(""+(scoutPoint.envData?.quadrant||''), opts),
                    createSimpleCell(""+(scoutPoint.envData?.clutterDescription||''), {...opts, paragraphAlignment: AlignmentType.LEFT }),
                    createSimpleCell(""+(scoutPoint.envData?.hvacDescription||''), {...opts, paragraphAlignment: AlignmentType.LEFT }),
                    createSimpleCell((scoutPoint.disabled?'DISABLED ':'')+(scoutPoint.envData?.pointDescription||''), {...opts, paragraphAlignment: AlignmentType.LEFT } ),
                  ]
                })
              }
            })            
          ]
        })
      ],
      footers: {
        default: new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            children: ["Page: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                            children: [" of ", PageNumber.TOTAL_PAGES],
                        }),
                    ],
                }),
            ],
        }),
    }
    });


    ////////////////////////////////////////////////////////////////////////////////
    //  floor plan diagram overlays
    let extraPlotterChildren = []
    for( let i = 2; i < plotterImageControls.length; i++ ) {
      const plotterImage = Media.addImage(doc, 
        plotterImageControls[i].plotterImageDataUrl, 
        this.mapDefs[i].wordDoc.width, 
        this.mapDefs[i].wordDoc.height)
      extraPlotterChildren.push(plotterImage)
      extraPlotterChildren.push(new TextRun("   "))
    }
    if ( !extraPlotterChildren.length ) {
      extraPlotterChildren.push(
        new TextRun({
         text: "Not Available",
         bold: false,
         size: this.DefaultFontSize,
         italics: true,
         color: 'gray'
        }).break()
      )
    }
    extraPlotterChildren[0].break().break()

    doc.addSection({
      size: {
        orientation: PageOrientation.LANDSCAPE,
      },      
      margins: pageMargins,
      children: [
        new Paragraph({
          style: 'sectionTitle',
          text: 'Path ' + this.getPathName() + " - Building and Floor Diagram Overlays",
          heading: HeadingLevel.HEADING_2,          
          //TODO vertical align images to top
          //alignment: AlignmentType.START,
          children: extraPlotterChildren
        })
      ],
      footers: {
        default: new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            children: ["Page: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                            children: [" of ", PageNumber.TOTAL_PAGES],
                        }),
                    ],
                }),
            ],
        }),
    }
    })

    ///////////////////////////////////////////////////////////////////////////////////
    // Photos (by scout point) 
    // NOTE: NOT FOR point-to-point or "Path Views" photos

    const getScoutPointTitle = (sp:ScoutPoint) => {
      const suffix = ' (' + sp.name + ')'
      switch(sp.type) {
        case 'HA':
          return sp.pointId + suffix
        case 'VIA':
          return 'VIA' + suffix
        case 'EX':
          return (sp.exPointId || 'EX') + suffix
      }
    }

    for( const pointAndPhotos of this.reportScoutPoints ) {

      for( const photoGrp of pointAndPhotos.photoGroups) {

        if ( photoGrp.groupName == 'point-to-point') {
          break;
        }
      
        let rows : TableRow[] = []
        let rowChildren: TableCell[] = []
        const maxPictureRunWidth = 480.0
        const maxPictureRunHeight = 270.0

        for( let index = 0; index < photoGrp.photos.length; index++) {
          let reportPhoto = photoGrp.photos[index]
          let dataUrl = reportPhoto.photoUrl          
          
          const bIsFirstColumn = index%2 == 0
          rowChildren.push( (await this.createPhotoTableCell(doc, 
            reportPhoto.photoDoc,dataUrl,bIsFirstColumn,
            maxPictureRunWidth,maxPictureRunHeight)).tableCell )

          const bIsLastPhoto = index+1 == photoGrp.photos.length
          if ( !bIsFirstColumn || bIsLastPhoto ) {
            console.log('adding row. index=' + index + ', rowChildren=' + rowChildren.length)
            rows.push(new TableRow({
              children: rowChildren,
              cantSplit: true
            }))
            rowChildren = []
          }

        }

        if ( rows.length == 0 ) {
          rows.push( new TableRow({
            children: [
              new TableCell({
                children: [
                  new Paragraph({text: 'No photos'})
                ]
              })
            ]
          }))
        }
        let table = new Table({
          rows: rows
        })
        const photoSectionExtraTextRuns = [];
        if ( pointAndPhotos.scoutPoint.disabled ) {
          photoSectionExtraTextRuns.push(new TextRun({
            text: '  Disabled (not a test point for placing calls)  ',
            italics: true,
            font: this.DefaultFont,
            shading: this.ParagraphHighlightShading
          }));
        }
        
        doc.addSection({
          size: {
            orientation: PageOrientation.LANDSCAPE,
          },      
          margins: pageMargins,
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: getScoutPointTitle(pointAndPhotos.scoutPoint) + ' - ' + photoGrp.title,
                  bold: true,              
                  font: this.DefaultFont,
                  size: 28,
                  shading: {
                    type: ShadingType.CLEAR,
                    color: "000000",
                    fill: pointAndPhotos.scoutPoint.color,
                  }
                }),
                ...photoSectionExtraTextRuns
              ]
            }),
            table            
          ],
          footers: {
            default: new Footer({
                children: [
                    new Paragraph({
                        alignment: AlignmentType.CENTER,
                        children: [
                            new TextRun({
                                children: ["Page: ", PageNumber.CURRENT],
                            }),
                            new TextRun({
                                children: [" of ", PageNumber.TOTAL_PAGES],
                            }),
                        ],
                    }),
                ],
            }),
          }
        })
      }
      }

      const startPtIndex = this.reportScoutPoints.findIndex(p=>p.scoutPoint.id==this.scoutPathStartPt.id)

      if ( this.reportScoutPoints.length && startPtIndex >= 0 ) {
        const photoFilter = (reportPhoto:ReportPhoto) => reportPhoto.photoDoc.group == 'point-to-point'
        const segmentBReportPoints = this.reportScoutPoints.slice(0,startPtIndex).reverse()
        const segmentAReportPoints = this.reportScoutPoints.slice(startPtIndex+1)

        //await this.addPhotoSection(doc,'Start Point Path Views',[haPointTable],photoFilter,[ this.reportScoutPoints[startPtIndex] ],pageMargins);
        await this.addPhotoSection(doc,'Segment B Path Views',[pathSegmentBTable],photoFilter,segmentBReportPoints,pageMargins);
        await this.addPhotoSection(doc,'Segment A Path Views',[pathSegmentATable],photoFilter,segmentAReportPoints,pageMargins);
      }

      console.log('report created. packing for download...')
      const blob = await Packer.toBlob(doc)
      let reportFileName = this.scoutTaskService.buildScoutPathFileName('.docx',this.scoutPathIndex+1,this.scoutPath.floor,this.building);
      saveAs(blob, reportFileName);    
    } finally {
      this.bBuildingWordDoc = false
    }
  }



  // Add a photo section to document where photos from all points and groups are included together (i.e. Path Views)
  async addPhotoSection( doc : Document, 
      title : string,
      headerChildren : (Table|Paragraph)[],
      photoFilter : (p:ReportPhoto) => boolean, 
      reportScoutPoints : ReportScoutPoint[],
      pageMargins : any ){

    let rowChildren: TableCell[] = []
    let singleRowTables : Table[] = [] // test for adding each row photos in its own table

    let imgWidthAcc = 0
    const maxPictureRunWidth = 480.0
    const maxPictureRunHeight = 450.0 //Anything over 270 means one row of pics per page
    const approxSpaceBetweenPics = 20
    console.log('Max pics row length: ' + ((maxPictureRunWidth*2)+approxSpaceBetweenPics))
    for ( const reportScoutPoint of reportScoutPoints ) {
      for( const reportPhotoGroup of reportScoutPoint.photoGroups) {
        const reportPhotos = reportPhotoGroup.photos.filter(photoFilter)
        for( var i = 0;  i < reportPhotos.length; i++) {
          const reportPhoto = reportPhotos[i]
          const { tableCell, photoResultInfo } = 
            await this.createPhotoTableCell(doc, reportPhoto.photoDoc, reportPhoto.photoUrl, true, maxPictureRunWidth, maxPictureRunHeight)          

          if ( photoResultInfo.pictureRun ) {
            imgWidthAcc += photoResultInfo.imgWidth + (singleRowTables.length?approxSpaceBetweenPics:0)
            console.log('imgWidthAcc: ' + imgWidthAcc + ', imgWidth: ' + photoResultInfo.imgWidth)
          }

          if ( imgWidthAcc > (maxPictureRunWidth*2)+approxSpaceBetweenPics ) {
            console.log('end of row')
            singleRowTables.push( new Table({ 
              rows: [ new TableRow({
                children: rowChildren,
                cantSplit: true
              })] 
            }))
            imgWidthAcc = photoResultInfo.imgWidth
            rowChildren = []
          }

          rowChildren.push( tableCell )
        }
      }
    }

    if ( !singleRowTables.length && !rowChildren.length ) {
      rowChildren.push( new TableCell({
        children: [ new Paragraph({
          children: [ new TextRun('No Photos').break().break() ]
        })]
      }))
    }

    if (rowChildren.length) {
      singleRowTables.push( new Table({ 
        rows: [ new TableRow({
          children: rowChildren,
          cantSplit: true
        })] 
      }))
    }

    let singleRowTablesWithParagraphSpacers = []
    singleRowTables.forEach(table=>{
      singleRowTablesWithParagraphSpacers.push( new Paragraph(''))
      singleRowTablesWithParagraphSpacers.push( table )
    })
    doc.addSection({
      size: {
        orientation: PageOrientation.LANDSCAPE,
      },

      margins: pageMargins,
      children: [
        new Paragraph({
          children: [
            new TextRun({
              text: title,
              bold: true,              
              font: this.DefaultFont,
              size: 28,
              //underline: { color: "black", type: UnderlineType.THICK }
            })
          ]
        }),
        ...headerChildren,
        new Paragraph(''), // separate headerChildren from table below or msword will combine as one table
        ...singleRowTablesWithParagraphSpacers
      ],
      footers: {
        default: new Footer({
            children: [
                new Paragraph({
                    alignment: AlignmentType.CENTER,
                    children: [
                        new TextRun({
                            children: ["Page: ", PageNumber.CURRENT],
                        }),
                        new TextRun({
                            children: [" of ", PageNumber.TOTAL_PAGES],
                        }),
                    ],
                }),
            ],
        }),
      }
    })
  }


  maxImageWidth = 1024.0
  maxImageHeight = 1024.0


  async createDataUrlForMsWordDoc( img: HTMLImageElement) {
    let canvas = this.alterPhotoCanvas.nativeElement as HTMLCanvasElement
    const maxWidth = this.maxImageWidth
    const maxHeight = this.maxImageHeight
    const srcWidth = img.width
    const srcHeight = img.height
    let scale = maxWidth/srcWidth;
    const scaledHeight = scale * srcHeight
    if ( scaledHeight > maxHeight ) {
      scale *= maxHeight/scaledHeight
    }
    canvas.width = scale*srcWidth; 
    canvas.height = scale*srcHeight; 
    var ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.drawImage(img, 0, 0, srcWidth, srcHeight, 0, 0, canvas.width, canvas.height);
    console.log('shrinking image.  size: ' + canvas.width + 'x' + canvas.height + ', quality=' + this.imageQuality)
    return canvas.toDataURL('image/jpeg', this.imageQuality)
  }


  async imageToDataUrl( img : HTMLImageElement ) {
    let canvas = this.alterPhotoCanvas.nativeElement as HTMLCanvasElement
    canvas.width = img.width 
    canvas.height = img.height
    var ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.drawImage(img,0,0);
    console.log('creating msword image.  size: ' + canvas.width + 'x' + canvas.height + ', quality=' + this.imageQuality)
    return canvas.toDataURL('image/jpeg', this.imageQuality)
  }

  //readonly maxPictureRunWidth = 520.0
  //readonly maxPictureRunHeight = 270.0

  async createPhotoTableCell( doc : Document, photoDoc : ScoutPointPhotoDoc, dataUrl : string, 
      bIsFirstColumn : boolean, maxPictureRunWidth: number, maxPictureRunHeight: number) {
    
    let photoResultInfo : { imgWidth?: number, imgHeight?: number, pictureRun?: PictureRun} = {}

    let pictureRun : PictureRun = null
    if ( dataUrl ) {
      try {
        const srcImg = await this.getAsImage(dataUrl)
        console.log(photoDoc.title)
        console.log('Image size: ' + srcImg.width + 'x' + srcImg.height)

        if ( srcImg.width > this.maxImageWidth || srcImg.height > this.maxImageHeight ) {
          dataUrl = await this.createDataUrlForMsWordDoc(srcImg)
        } else {
          dataUrl = await this.imageToDataUrl(srcImg)
        }
        
        var imgWidth, imgHeight

        imgWidth = maxPictureRunWidth
        imgHeight =  maxPictureRunWidth*(srcImg.height/srcImg.width)
        if ( imgHeight > maxPictureRunHeight ) {
          const scale = maxPictureRunHeight/imgHeight
          imgWidth*=scale
          imgHeight*=scale
        }
        
        console.log('creating msword image.  size: ' + imgWidth + 'x' + imgHeight)
        pictureRun = Media.addImage(doc, dataUrl, imgWidth, imgHeight)
        photoResultInfo = { imgWidth, imgHeight, pictureRun }

        //console.log( 'photoInfoResult: ' + JSON.stringify(photoInfoResult))
      } catch (error) {
        console.error(error)
      }
    }

    let photoTableCellChildren = [
      new Paragraph({ 
        style:'tableTitle', 
        children: [
          new TextRun({
            text: photoDoc.title,                  
          })
        ]
      }),
      new Paragraph({children: [
        pictureRun ?
          pictureRun : 
          new TextRun({
            text: 'Error: Photo defined but could not be loaded'
          })
      ]})
    ]

    if ( photoDoc.notes ) {
      photoTableCellChildren.push(new Paragraph({ text: photoDoc.notes}))
    }
    
    
    const tableCell = new TableCell({
      children: photoTableCellChildren,
      borders: this.noBorderTableCellOption,
      margins: { top: 180, right: bIsFirstColumn ? 380 : 0, bottom: 80, left: !bIsFirstColumn ? 380 : 0 }
    })

    return { tableCell, photoResultInfo }
  }


  getPathName() {    
    const buildingId = this.scoutTaskDoc?.buildingId
    const pathIndex = this.pathNumber ? new String(this.pathNumber).padStart(2,'0') : ''
    
    return (buildingId && pathIndex) ? `${buildingId}P${pathIndex}` : ''
  }


  getAsImage(file) : Promise<HTMLImageElement> {
    return new Promise (function (resolved, rejected) {
      if ( !file ) {
        rejected('image url is null')
      } else {
        var i = new Image()
        i.onload = function(){
          console.log('image loaded from url')
          resolved(i)
        };
        i.onerror = function() {
          rejected('Image create failed for src data url')
        }
        i.src = file
      }
    })
  }

  // Showing all photos in browser at once can make page unresponsive so display spinner before unresponsive
  bEnablingPhotosPreview = false
  async enablePhotosPreview() {
    this.bEnablingPhotosPreview = true
    await timer(100).toPromise()
    this.bEnablingPhotosPreview = false
    this.bShowPhotos = true
  }
}
